import {FC, useState} from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import {CGERegionResult} from '../core/_models'

type CGERegionsTableProps = {
  rows: CGERegionResult[]
  focusCode: string
}

const columnHelper = createColumnHelper<CGERegionResult>()

const pctCell = (value: number) => (
  <span className={value >= 0 ? 'text-success fw-bolder' : 'text-danger fw-bolder'}>
    {value >= 0 ? '+' : ''}
    {value}%
  </span>
)

const CGERegionsTable: FC<CGERegionsTableProps> = ({rows, focusCode}) => {
  const [sorting, setSorting] = useState<SortingState>([{id: 'welfare_ev_usd_mn', desc: false}])

  const columns = [
    columnHelper.accessor('name', {
      header: 'Region',
      cell: (info) => (
        <span className={info.row.original.code === focusCode ? 'fw-bolder text-primary' : ''}>
          {info.getValue()}
        </span>
      ),
    }),
    columnHelper.accessor('welfare_ev_usd_mn', {
      header: 'Welfare (US$M, EV)',
      cell: (info) => pctCell(info.getValue()),
      meta: {cellClass: 'text-end'},
    }),
    columnHelper.accessor('terms_of_trade_pct', {
      header: 'Terms of trade (%)',
      cell: (info) => pctCell(info.getValue()),
      meta: {cellClass: 'text-end'},
    }),
    columnHelper.accessor('real_gdp_pct', {
      header: 'Real GDP (%)',
      cell: (info) => pctCell(info.getValue()),
      meta: {cellClass: 'text-end'},
    }),
  ]

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {sorting},
  })

  return (
    <div className='table-responsive'>
      <table className='gy-3 table table-striped table-bordered table-hover'>
        <thead className='table-dark'>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className='fw-normal py-4 text-center align-middle'>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className='cursor-pointer select-none'
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  <span className='ms-2'>
                    {{
                      asc: <i className='fa-solid fa-sort-up'></i>,
                      desc: <i className='fa-solid fa-sort-down'></i>,
                      false: <i className='fa-solid fa-sort'></i>,
                    }[header.column.getIsSorted() as string] ?? null}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className='align-middle text-center'>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export {CGERegionsTable}
